import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import './DailyReceipt.css';

const MODES = ['Cash', 'Card', 'UPI', 'Bank Transfer', 'Cheque'];
const EMPTY = {
  receipt_date: today(),
  items: [{ mode: 'Cash', bank_id: '', description: '', amount: '', file: null }],
};

export default function DailyReceipt() {
  const { user, canAccess, can } = useAuth();
  const { data: receipts, loading, error, reload } = useApi(() => api.dailyReceipts.list(), []);
  const { data: bankData, loading: banksLoading } = useApi(() => api.banks.list(), [], { banks: [] });
  const banks = bankData?.banks || [];

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirm, setConfirm] = useState(null);

  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };
  const isToday = (d) => d === today();

  const canUpdate = can('daily_receipts', 'update');

  // Auto-select bank for the first item when form opens or banks load
  React.useEffect(() => {
    if (showForm && banks.length > 0 && form.items.length === 1 && !form.items[0].bank_id) {
      const filtered = getFilteredBanks(form.items[0].mode);
      const def = filtered.find(b => Number(b.is_default) === 1) || filtered[0];
      if (def) {
        const it = [...form.items];
        it[0].bank_id = def.id;
        setForm(prev => ({ ...prev, items: it }));
      }
    }
  }, [showForm, banks]);

  // Helper to filter banks per item
  const getFilteredBanks = (mode) => {
    const cash = banks.filter(b => b.account_type === 'Cash' || b.name.toLowerCase().includes('cash'));
    const nonCash = banks.filter(b => b.account_type !== 'Cash' && !b.name.toLowerCase().includes('cash'));
    if (mode === 'Cash') return cash.length > 0 ? cash : banks;
    return nonCash.length > 0 ? nonCash : banks;
  };

  const addItem = () => {
    const last = form.items[form.items.length - 1];
    setForm({
      ...form,
      items: [...form.items, { mode: last?.mode || 'Cash', bank_id: last?.bank_id || '', description: '', amount: '', file: null }]
    });
  };

  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const updateItem = (idx, f, v) => {
    const it = [...form.items];
    it[idx][f] = v;
    if (f === 'mode') {
      const filtered = getFilteredBanks(v);
      const stillValid = filtered.find(b => b.id === Number(it[idx].bank_id));
      if (!stillValid) {
        const defaultBank = filtered.find(b => Number(b.is_default) === 1) || filtered[0];
        it[idx].bank_id = defaultBank ? defaultBank.id : '';
      }
    }
    setForm({ ...form, items: it });
  };

  const handleSave = async () => {
    const validItems = form.items.filter(it => it.description && Number(it.amount) > 0 && it.bank_id);
    if (validItems.length === 0) { notify('❌ Please fill all fields for at least one item'); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('receipt_date', form.receipt_date);

      // Map items and attach files individually
      const itemsPayload = validItems.map((it, idx) => {
        if (it.file) {
          fd.append(`file_${idx}`, it.file);
        }
        return {
          mode: it.mode,
          bank_id: it.bank_id,
          description: it.description,
          amount: it.amount,
          has_file: !!it.file,
          file_index: idx
        };
      });

      fd.append('items', JSON.stringify(itemsPayload));

      await api.dailyReceipts.create(fd);
      notify('✅ Receipt saved'); setShowForm(false); setForm(EMPTY); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleEditSave = async () => {
    if (!editRow.bank_id) { notify('❌ Please select a Bank/Account'); return; }
    setSaving(true);
    try {
      await api.dailyReceipts.update(editRow.id, {
        description: editRow.description,
        mode: editRow.mode,
        bank_id: parseInt(editRow.bank_id),
        amount: parseFloat(editRow.amount)
      });
      notify('✅ Updated'); setEditRow(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try { await api.dailyReceipts.remove(confirm.row.id); notify('✅ Deleted'); reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  const handleViewAttachment = async (id) => {
    try {
      const blob = await api.dailyReceipts.downloadAttachment(id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      notify('❌ Error: ' + e.message);
    }
  };

  const total = (receipts || []).reduce((s, r) => s + Number(r.amount || 0), 0);
  
  if (!canAccess('daily_receipts')) {
    return (
      <div className="daily-receipt-page">
        <ErrBanner msg="Access Denied: You do not have permission to access daily receipts." />
      </div>
    );
  }

  return (
    <div className="daily-receipt-page">
      <div className="page-header">
        <h2 className="page-title">Daily Receipts</h2>
        {can('daily_receipts', 'create') && (
          <button className="btn btn-primary btn-sm" onClick={() => {
            setShowForm(!showForm);
            setEditRow(null);
            setForm(EMPTY);
          }}>
            {showForm ? '✕ Cancel' : '＋ Add Receipt'}
          </button>
        )}
      </div>
      {msg && <div style={{ padding: '6px 12px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 6, marginBottom: 8, fontSize: 13 }}>{msg}</div>}

      {showForm && (
        <div className="section-card">
          <div className="section-header">New Receipt Entry</div>
          <div style={{ padding: '0 16px 16px' }}>
            <div className="form-row" style={{ marginBottom: 16 }}>
              <div className="form-group" style={{ maxWidth: 200 }}><label>Date</label>
                <input type="date" className="form-control" value={form.receipt_date} onChange={e => setForm({ ...form, receipt_date: e.target.value })} />
              </div>
            </div>

            <table className="data-table" style={{ marginBottom: 16 }}>
              <thead>
                <tr>
                  <th style={{ width: '12%' }}>Mode</th>
                  <th>Description</th>
                  <th style={{ width: '20%' }}>Account / Bank</th>
                  <th style={{ width: '12%' }}>Amount (₹)</th>
                  <th style={{ width: '15%' }}>Attachment</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => (
                  <tr key={idx}>
                    <td>
                      <select className="form-control" value={it.mode} onChange={e => updateItem(idx, 'mode', e.target.value)}>
                        {MODES.map(m => <option key={m}>{m}</option>)}
                      </select>
                    </td>
                    <td><input className="form-control" value={it.description} placeholder="e.g. Sales collection" onChange={e => updateItem(idx, 'description', e.target.value)} /></td>
                    <td>
                      <select className="form-control" value={it.bank_id} onChange={e => updateItem(idx, 'bank_id', e.target.value)}>
                        <option value="">{banksLoading ? '...' : '-- Select --'}</option>
                        {getFilteredBanks(it.mode).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                    </td>
                    <td><input type="number" className="form-control" value={it.amount} onChange={e => updateItem(idx, 'amount', e.target.value)} /></td>
                    <td>
                      <div className="custom-file-upload-small">
                        {it.file ? `📄 ${it.file.name.substring(0, 10)}...` : '📁 File'}
                        <input type="file" onChange={e => updateItem(idx, 'file', e.target.files[0])} />
                      </div>
                    </td>
                    <td>
                      {form.items.length > 1 && (
                        <button className="btn btn-sm" style={{ color: 'var(--danger)' }} onClick={() => removeItem(idx)}>🗑️</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button className="btn btn-secondary btn-sm" onClick={addItem}>＋ Add Another Row</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : '💾 Save Receipt'}</button>
            </div>
          </div>
        </div>
      )}

      {editRow && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ Edit Receipt — {editRow.receipt_no} — {fmtDate(editRow.receipt_date)}</div>
          <div className="form-row">
            <div className="form-group"><label>Mode</label>
              <select className="form-control" value={editRow.mode} onChange={e => setEditRow({ ...editRow, mode: e.target.value })}>
                {MODES.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Account / Bank</label>
              <select className="form-control" value={editRow.bank_id} onChange={e => setEditRow({ ...editRow, bank_id: e.target.value })}>
                <option value="">{banksLoading ? '...' : '-- Select --'}</option>
                {getFilteredBanks(editRow.mode).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Description</label>
              <input className="form-control" value={editRow.description} onChange={e => setEditRow({ ...editRow, description: e.target.value })} />
            </div>
            <div className="form-group"><label>Amount (₹)</label>
              <input type="number" className="form-control" value={editRow.amount} onChange={e => setEditRow({ ...editRow, amount: e.target.value })} />
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleEditSave} disabled={saving}>{saving ? 'Saving…' : '💾 Update'}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {loading && <Spinner />}
      {error && <ErrBanner msg={error} />}
      {!loading && (
        <div className="section-card">
          <div className="section-header">Receipt Records — Total: ₹{fmtNum(total)}</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th>#</th><th style={{ textAlign: 'left' }}>Date</th><th style={{ textAlign: 'left' }}>Receipt No</th>
                <th style={{ textAlign: 'left' }}>Mode</th><th style={{ textAlign: 'left' }}>Account</th>
                <th style={{ textAlign: 'left' }}>Description</th>
                <th style={{ textAlign: 'right' }}>Amount (₹)</th><th style={{ textAlign: 'center' }}>Actions</th>
              </tr></thead>
              <tbody>
                {(receipts || []).map((r, i) => (
                  <tr key={r.id}>
                    <td>{i + 1}</td><td>{fmtDate(r.receipt_date)}</td>
                    <td style={{ fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace' }}>
                      {r.receipt_no}
                      {r.file_path && (
                        <button onClick={() => handleViewAttachment(r.id)} title="View Attachment" style={{ marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>📎</button>
                      )}
                    </td>
                    <td><span className="badge badge-info">{r.mode}</span></td>
                    <td style={{ fontWeight: 600 }}>{r.bank_name || <span style={{ color: '#ccc', fontStyle: 'italic' }}>Unallocated</span>}</td>
                    <td>{r.description}</td>
                    <td className="right-align">₹{fmtNum(r.amount)}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {can('daily_receipts', 'update') && (isToday(r.receipt_date) || canUpdate) && (<button title="Edit" style={btnEdit} onClick={() => setEditRow({ ...r })}>✏️</button>
                      )}
                      {can('daily_receipts', 'delete') && (
                        <button title="Delete" style={btnDel} onClick={() => setConfirm({ row: r })}>🗑️</button>
                      )}
                    </td>
                  </tr>
                ))}
                {(receipts || []).length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No receipts recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title="Delete Receipt"
        message={`Delete receipt ${confirm?.row?.receipt_no}? This record will be soft-deleted.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

const btnEdit = { background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginRight: 4, fontSize: 13 };
const btnDel = { background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 };
